import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button, LoadingSpinner, SummaryLevelSelector, FileUpload } from '@/ui';
import { SummaryResult } from './SummaryResult';
import VideoSummaryResult from './VideoSummaryResult';
import { SummaryLevel } from '@/ui';
import { useTotalTimeSaved } from '../hooks/useTotalTimeSaved';
import { useHistoryCache } from '../hooks/useHistoryCache';
import { useToastContext } from './ToastProvider';
import HistorySlider from './HistorySlider';
import ImageSlider from './ImageSlider';

interface ImageData {
  url: string;
  alt?: string;
  title?: string;
}

interface SummaryResponse {
  summary: string;
  originalLength: number;
  summaryLength: number;
  compressionRatio: number;
  level: SummaryLevel['type'];
  timestamp: Date;
  url?: string;
  fileName?: string;
  images?: ImageData[];
  timeToReadOriginal?: number;
  timeToReadSummary?: number;
  timeSaved?: number;
  timeSavedPercentage?: number;
  efficiency?: string;
  timeSavedFormatted?: string;
  totalTimeSaved?: number;
  totalTimeSavedFormatted?: string;
  videoData?: {
    title: string;
    keyPoints: string[];
    duration: string;
    transcriptSource: 'official' | 'whisper';
    processingTimeMs: number;
    videoUrl: string;
  };
}

const SummaryForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { showError, showSuccess } = useToastContext();
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { addTimeSaved } = useTotalTimeSaved();
  const { history, findInCache, addToHistory, removeFromHistory } =
    useHistoryCache();
  const [level, setLevel] = useState<SummaryLevel['type']>('bullet-points');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<SummaryResponse | null>(null);
  const [mode, setMode] = useState<'text' | 'url' | 'file'>('text');
  const [fileSubMode, setFileSubMode] = useState<'document' | 'image'>(
    'document'
  );
  const [isFromCache, setIsFromCache] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Fonction pour détecter si une URL est une URL YouTube
  const isValidYouTubeUrl = (url: string): boolean => {
    const youtubeRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]+/;
    return youtubeRegex.test(url);
  };

  // Auto-resize textarea when content changes
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(200, textarea.scrollHeight) + 'px';
    }
  }, [content]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setResult(null);
    setIsFromCache(false);

    try {
      // Déterminer le contenu à analyser selon le mode
      let currentContent = '';
      let currentUrl: string | undefined;
      let currentFileName: string | undefined;

      if (mode === 'text') {
        if (!content.trim()) {
          showError('Contenu requis', 'Veuillez saisir du texte à résumer');
          return;
        }
        if (content.length < 50) {
          showError(
            'Texte trop court',
            `Minimum requis : 50 caractères, vous en avez ${content.length}. Veuillez ajouter plus de contenu.`
          );
          return;
        }
        if (content.length > 20000) {
          showError('Texte trop long', t('summary.errors.contentTooLong'));
          return;
        }
        currentContent = content.trim();
      } else if (mode === 'url') {
        if (!url.trim()) {
          showError('URL requise', 'Veuillez saisir une URL à résumer');
          return;
        }
        currentContent = url.trim();
        currentUrl = url.trim();
      } else if (mode === 'file') {
        if (!selectedFile) {
          showError(
            'Fichier requis',
            'Veuillez sélectionner un fichier à résumer'
          );
          return;
        }
        currentContent = selectedFile.name; // Utiliser le nom de fichier comme identifiant
        currentFileName = selectedFile.name;
      } else if (mode === 'file' && fileSubMode === 'image') {
        if (!selectedFile) {
          showError(
            'Image requise',
            'Veuillez sélectionner une image à analyser'
          );
          return;
        }
        currentContent = selectedFile.name;
        currentFileName = selectedFile.name;
      }

      // Vérifier le cache d'abord
      const cachedResult = findInCache(
        currentContent,
        currentUrl,
        currentFileName,
        level
      );

      if (cachedResult) {
        // Utiliser le résultat du cache
        const resultData = {
          ...cachedResult.result,
          level: cachedResult.level,
          timestamp: cachedResult.timestamp,
          url: cachedResult.url,
          fileName: cachedResult.fileName,
        };
        setResult(resultData);
        setIsFromCache(true);

        // Ajouter le temps gagné (même si c'est du cache)
        if (resultData.timeSaved && resultData.timeSaved > 0) {
          addTimeSaved(resultData.timeSaved);
        }
        return;
      }

      // Si pas en cache, faire l'appel API
      let response;
      if (mode === 'text') {
        response = await axios.post(`${API_BASE_URL}/summary/text`, {
          content: currentContent,
          level,
          language: i18n.language,
        });
      } else if (mode === 'url') {
        // Vérifier si c'est une URL YouTube
        if (isValidYouTubeUrl(currentContent)) {
          // Appeler l'API vidéo pour les URLs YouTube
          response = await axios.post(`${API_BASE_URL}/video-summary`, {
            videoUrl: currentContent,
            language: i18n.language,
            level: level,
          });
        } else {
          // Appeler l'API URL normale pour les autres URLs
          response = await axios.post(`${API_BASE_URL}/summary/url`, {
            url: currentContent,
            level,
            language: i18n.language,
          });
        }
      } else if (mode === 'file') {
        if (fileSubMode === 'image') {
          // Analyse d'image avec GPT Vision
          const formData = new FormData();
          formData.append('image', selectedFile!);
          formData.append('level', 'bullet-points');
          formData.append('analysisType', 'auto');
          formData.append('language', i18n.language);

          response = await axios.post(
            `${API_BASE_URL}/image-analysis`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
        } else {
          // Lecture de document normal
          const formData = new FormData();
          formData.append('file', selectedFile!);
          formData.append('level', level);
          formData.append('language', i18n.language);

          response = await axios.post(
            `${API_BASE_URL}/summary/file`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
        }
      }

      if (response) {
        let resultData = response.data;

        // Si c'est une réponse vidéo, adapter la structure
        if (mode === 'url' && isValidYouTubeUrl(currentContent)) {
          // Adapter la réponse vidéo au format attendu par SummaryForm
          resultData = {
            summary: resultData.summary.summary,
            originalLength: resultData.originalLength,
            summaryLength: resultData.summaryLength,
            compressionRatio: resultData.compressionRatio,
            level: 'bullet-points', // Les vidéos utilisent toujours les points clés
            timestamp: new Date(),
            url: currentUrl,
            images: [], // Pas d'images pour les vidéos
            timeToReadOriginal: resultData.timeToReadOriginal,
            timeToReadSummary: resultData.timeToReadSummary,
            timeSaved: resultData.timeSaved,
            timeSavedPercentage: resultData.timeSavedPercentage,
            efficiency: resultData.efficiency,
            timeSavedFormatted: resultData.timeSavedFormatted,
            totalTimeSaved: resultData.totalTimeSaved,
            totalTimeSavedFormatted: resultData.totalTimeSavedFormatted,
            // Données spécifiques aux vidéos
            videoData: {
              title: resultData.summary.title,
              keyPoints: resultData.summary.keyPoints,
              duration: resultData.summary.duration,
              transcriptSource: resultData.summary.transcriptSource,
              processingTimeMs: resultData.processingTimeMs,
              videoUrl: resultData.videoUrl,
            },
          };
        } else if (mode === 'file' && fileSubMode === 'image') {
          // Adapter la réponse d'analyse d'image au format attendu par SummaryForm
          resultData = {
            summary: resultData.summary,
            originalLength: resultData.wordCount,
            summaryLength: resultData.summary.length,
            compressionRatio:
              resultData.wordCount > 0
                ? resultData.summary.length / resultData.wordCount
                : 0,
            level: 'bullet-points' as SummaryLevel['type'],
            timestamp: new Date(),
            fileName: resultData.metadata.fileName,
            timeSaved: resultData.timeSaved,
            timeSavedFormatted: resultData.timeSaved,
            totalTimeSaved: 0,
            totalTimeSavedFormatted: '0s',
            // Données spécifiques à l'analyse d'image
            detectedType: resultData.detectedType,
            objectName: resultData.objectName,
            confidence: resultData.confidence,
            detailedPoints: resultData.detailedPoints,
            detailedInfo: resultData.detailedInfo,
            wordCount: resultData.wordCount,
            imageAnalysisData: {
              detectedType: resultData.detectedType,
              objectName: resultData.objectName,
              confidence: resultData.confidence,
              detailedPoints: resultData.detailedPoints,
              detailedInfo: resultData.detailedInfo,
              metadata: resultData.metadata,
            },
          };
        }

        setResult(resultData);

        // Les images sont maintenant incluses dans la réponse API
        const apiImages =
          resultData.images?.map((img: any, index: number) => ({
            id: `${Date.now()}-${index}`,
            url: img.url,
            alt: img.alt || '',
            title: img.title || '',
            sourceUrl: currentUrl || '',
            description: img.description || '',
          })) || [];

        // Ajouter au cache
        addToHistory(
          currentContent,
          mode,
          level,
          {
            summary: resultData.summary,
            originalLength: resultData.originalLength,
            summaryLength: resultData.summaryLength,
            compressionRatio: resultData.compressionRatio,
            timeToReadOriginal: resultData.timeToReadOriginal,
            timeToReadSummary: resultData.timeToReadSummary,
            timeSaved: resultData.timeSaved,
            timeSavedPercentage: resultData.timeSavedPercentage,
          },
          currentUrl,
          currentFileName,
          apiImages
        );

        // Ajouter le temps gagné au total si disponible (en secondes)
        if (resultData.timeSaved && resultData.timeSaved > 0) {
          addTimeSaved(resultData.timeSaved);
        }

        // Afficher un toast de succès
        showSuccess(
          t('summary.result.title'),
          `${t('summary.result.title')} ${t('common.success', 'créé avec succès')}. ${resultData.timeSavedFormatted ? `${t('summary.result.stats.timeSaved')} : ${resultData.timeSavedFormatted}` : ''}`
        );
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        t('summary.errors.genericError');
      showError('Erreur de génération', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler pour charger un élément depuis l'historique
  const handleHistorySelect = (item: any) => {
    const resultData = {
      ...item.result,
      level: item.level,
      timestamp: item.timestamp,
      url: item.url,
      fileName: item.fileName,
    };

    setResult(resultData);
    setIsFromCache(true);

    // Mettre à jour le formulaire avec les données de l'historique
    if (item.mode === 'text') {
      setContent(item.content);
      setMode('text');
    } else if (item.mode === 'url') {
      setUrl(item.content);
      setMode('url');
    } else if (item.mode === 'file') {
      setMode('file');
    } else if (item.mode === 'image') {
      setMode('file');
      setFileSubMode('image');
    }
    setLevel(item.level);
  };

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-1 mb-4 sm:mb-6 lg:mb-8">
        <div className="flex w-full gap-1">
          <button
            type="button"
            onClick={() => setMode('text')}
            className={`flex-1 flex items-center justify-center space-x-1 sm:space-x-2 py-2 sm:py-3 lg:py-4 px-2 sm:px-3 lg:px-6 rounded-lg sm:rounded-xl font-medium transition-all duration-300 min-h-[44px] sm:min-h-[48px] lg:min-h-[56px] ${
              mode === 'text'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transform scale-[1.02]'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="font-semibold text-xs sm:text-sm lg:text-base hidden sm:inline">
              {t('summary.form.modes.text')}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setMode('url')}
            className={`flex-1 flex items-center justify-center space-x-1 sm:space-x-2 py-2 sm:py-3 lg:py-4 px-2 sm:px-3 lg:px-6 rounded-lg sm:rounded-xl font-medium transition-all duration-300 min-h-[44px] sm:min-h-[48px] lg:min-h-[56px] ${
              mode === 'url'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transform scale-[1.02]'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
            <span className="font-semibold text-xs sm:text-sm lg:text-base hidden sm:inline">
              {t('summary.form.modes.url')}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setMode('file')}
            className={`flex-1 flex items-center justify-center space-x-1 sm:space-x-2 py-2 sm:py-3 lg:py-4 px-2 sm:px-3 lg:px-6 rounded-lg sm:rounded-xl font-medium transition-all duration-300 min-h-[44px] sm:min-h-[48px] lg:min-h-[56px] ${
              mode === 'file'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg transform scale-[1.02]'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <span className="font-semibold text-xs sm:text-sm lg:text-base hidden sm:inline">
              {t('summary.form.modes.file')}
            </span>
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Content Input */}
        {mode === 'text' ? (
          <div className="space-y-3">
            <label
              htmlFor="content"
              className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3"
            >
              {t('summary.form.contentLabel')}
            </label>
            <div className="relative">
              <textarea
                ref={textareaRef}
                id="content"
                value={content}
                onChange={(e) => {
                  if (e.target.value.length <= 20000) {
                    setContent(e.target.value);
                  }
                }}
                placeholder={'Lorem ipsum dolor sit amet...'}
                rows={6}
                maxLength={20000}
                className={`w-full px-3 sm:px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none bg-white dark:bg-gray-700 shadow-sm min-h-[150px] sm:min-h-[200px] text-sm sm:text-base text-gray-900 dark:text-gray-100 ${
                  content.length >= 20000
                    ? 'border-red-300 focus:ring-red-500 dark:border-red-600'
                    : content.length > 18000
                      ? 'border-orange-300 focus:ring-orange-500 dark:border-orange-600'
                      : content.length < 50 && content.length > 0
                        ? 'border-yellow-300 focus:ring-yellow-500 dark:border-yellow-600'
                        : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={isLoading}
                style={{ minHeight: '150px' }}
              />
              <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3">
                <div
                  className={`text-xs px-2 py-1 rounded-full ${
                    content.length >= 20000
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                      : content.length > 18000
                        ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400'
                        : content.length > 15000
                          ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400'
                          : content.length < 50 && content.length > 0
                            ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  <span className="hidden sm:inline">
                    {content.length.toLocaleString()} / 20 000{' '}
                    {t('summary.form.charactersCount')}
                  </span>
                  <span className="sm:hidden">
                    {content.length.toLocaleString()}/20k
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : mode === 'url' ? (
          <div className="space-y-3">
            <label
              htmlFor="url"
              className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3"
            >
              {t('summary.form.url')}
            </label>
            <div className="relative">
              <input
                type="url"
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t(
                  'summary.form.urlPlaceholder',
                  'https://exemple.com/article'
                )}
                className="w-full px-3 sm:px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-700 shadow-sm text-sm sm:text-base text-gray-900 dark:text-gray-100"
                disabled={isLoading}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Sous-navigation pour les types de fichiers */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg sm:rounded-xl p-1">
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setFileSubMode('document')}
                  className={`flex-1 flex items-center justify-center space-x-1 sm:space-x-2 py-2 sm:py-3 px-2 sm:px-3 lg:px-4 rounded-md sm:rounded-lg font-medium transition-all duration-200 ${
                    fileSubMode === 'document'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">
                    Lecture
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setFileSubMode('image')}
                  className={`flex-1 flex items-center justify-center space-x-1 sm:space-x-2 py-2 sm:py-3 px-2 sm:px-3 lg:px-4 rounded-md sm:rounded-lg font-medium transition-all duration-200 ${
                    fileSubMode === 'image'
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium hidden sm:inline">
                    Analyse d'image
                  </span>
                </button>
              </div>
            </div>

            {/* Contenu selon le sous-mode */}
            {fileSubMode === 'document' ? (
              <div className="space-y-3">
                <span className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Documents et images (PDF, DOC, TXT, JPG, PNG)
                </span>
                <FileUpload
                  onFileSelect={(files) => setSelectedFile(files[0] || null)}
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  maxSize={10 * 1024 * 1024} // 10MB
                  className="w-full"
                  texts={{
                    selectFile: t('summary.form.fileUpload.selectFile'),
                    dragDropOrBrowse: t(
                      'summary.form.fileUpload.dragDropOrBrowse'
                    ),
                    pressEnterOrSpace: t(
                      'summary.form.fileUpload.pressEnterOrSpace'
                    ),
                    maxSize: t('summary.form.fileUpload.maxSize'),
                    fileTooLarge: t('summary.form.fileUpload.fileTooLarge'),
                    fileTypeNotAccepted: t(
                      'summary.form.fileUpload.fileTypeNotAccepted'
                    ),
                    selectFileLabel: t(
                      'summary.form.fileUpload.selectFileLabel'
                    ),
                    uploadZoneLabel: t(
                      'summary.form.fileUpload.uploadZoneLabel'
                    ),
                  }}
                />
                {selectedFile && (
                  <div className="mt-4 sm:mt-6 p-4 sm:p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl sm:rounded-2xl shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 dark:bg-green-900/30 rounded-lg sm:rounded-xl flex items-center justify-center">
                            <svg
                              className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-semibold text-green-800 dark:text-green-200">
                            Document sélectionné
                          </h4>
                          <p className="text-sm text-green-700 dark:text-green-300 font-medium truncate">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="flex-shrink-0 p-2 text-green-600 hover:text-green-700 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                      >
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <span className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Images pour analyse IA (JPG, PNG, WebP)
                </span>
                <FileUpload
                  onFileSelect={(files) => setSelectedFile(files[0] || null)}
                  accept=".jpg,.jpeg,.png,.webp"
                  maxSize={20 * 1024 * 1024} // 20MB
                  className="w-full"
                  texts={{
                    selectFile: t('summary.form.fileUpload.selectFile'),
                    dragDropOrBrowse: t(
                      'summary.form.fileUpload.dragDropOrBrowse'
                    ),
                    pressEnterOrSpace: t(
                      'summary.form.fileUpload.pressEnterOrSpace'
                    ),
                    maxSize: t('summary.form.fileUpload.maxSize'),
                    fileTooLarge: t('summary.form.fileUpload.fileTooLarge'),
                    fileTypeNotAccepted: t(
                      'summary.form.fileUpload.fileTypeNotAccepted'
                    ),
                    selectFileLabel: t(
                      'summary.form.fileUpload.selectFileLabel'
                    ),
                    uploadZoneLabel: t(
                      'summary.form.fileUpload.uploadZoneLabel'
                    ),
                  }}
                />
                {selectedFile && (
                  <div className="mt-4 sm:mt-6 p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl sm:rounded-2xl shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg sm:rounded-xl flex items-center justify-center">
                            <svg
                              className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                            Image sélectionnée
                          </h4>
                          <p className="text-sm text-blue-700 dark:text-blue-300 font-medium truncate">
                            {selectedFile.name}
                          </p>
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="flex-shrink-0 p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                      >
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Summary Level Selector - Only show for text, url, and file modes (not for image sub-mode) */}
        {!(mode === 'file' && fileSubMode === 'image') && (
          <SummaryLevelSelector
            value={level}
            onChange={setLevel}
            translations={{
              ultra_short: t('summary.form.levels.ultra_short'),
              ultra_short_desc: t('summary.form.levels.ultra_short_desc'),
              points_cles: t('summary.form.levels.points_cles'),
              points_cles_desc: t('summary.form.levels.points_cles_desc'),
              detailed: t('summary.form.levels.detailed'),
              detailed_desc: t('summary.form.levels.detailed_desc'),
              maxCharacters: t('summary.form.maxCharacters'),
              characters: t('summary.form.characters'),
              tip: {
                title: t('summary.form.tip.title'),
                content: t('summary.form.tip.content'),
              },
            }}
          />
        )}

        {/* Submit Button - Show for all modes */}
        <div className="pt-4">
          <Button
            type="submit"
            disabled={
              isLoading ||
              (mode === 'text'
                ? !content.trim()
                : mode === 'url'
                  ? !url.trim()
                  : !selectedFile)
            }
            loading={isLoading}
            className="w-full py-3 sm:py-4 text-base sm:text-lg font-semibold bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <span className="text-sm sm:text-base">
                  {t('summary.errors.processing')}
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                <span className="text-sm sm:text-base">
                  {mode === 'file' && fileSubMode === 'image'
                    ? "Analyser l'image"
                    : t('summary.form.submit', 'Générer le résumé')}
                </span>
              </div>
            )}
          </Button>
        </div>
      </form>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8 sm:py-12">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl mb-4">
            <LoadingSpinner size="lg" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
            {t('summary.errors.processing')}
          </h3>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {t('summary.errors.aiAnalysis')}
          </p>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div className="space-y-4">
          {isFromCache && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
              <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                {t('history.loadedFromCache')}
              </span>
            </div>
          )}

          {result.videoData ? (
            <VideoSummaryResult
              data={{
                summary: {
                  title: result.videoData.title,
                  summary: result.summary,
                  keyPoints: result.videoData.keyPoints,
                  duration: result.videoData.duration,
                  transcriptSource: result.videoData.transcriptSource,
                },
                videoUrl: result.videoData.videoUrl,
                processingTimeMs: result.videoData.processingTimeMs,
                // Métriques de temps
                originalLength: result.originalLength || 0,
                summaryLength: result.summaryLength || 0,
                compressionRatio: result.compressionRatio || 0,
                timeToReadOriginal: result.timeToReadOriginal || 0,
                timeToReadSummary: result.timeToReadSummary || 0,
                timeSaved: result.timeSaved || 0,
                timeSavedPercentage: result.timeSavedPercentage || 0,
                efficiency: result.efficiency || 'Correcte',
                timeSavedFormatted: result.timeSavedFormatted || '0s',
                totalTimeSaved: result.totalTimeSaved || 0,
                totalTimeSavedFormatted: result.totalTimeSavedFormatted || '0s',
              }}
            />
          ) : (
            <SummaryResult result={result} />
          )}
        </div>
      )}

      {/* Image Slider - Only for current result with images */}
      {result?.images && result.images.length > 0 && (
        <ImageSlider
          images={result.images.map((img, index) => ({
            id: `current-${index}`,
            url: img.url,
            alt: img.alt || '',
            title: img.title || '',
            sourceUrl: result.url || '',
            description: '',
          }))}
          className="mt-8"
        />
      )}

      {/* History Slider */}
      {history.length > 0 && (
        <HistorySlider
          history={history}
          onSelectItem={handleHistorySelect}
          onRemoveItem={removeFromHistory}
          className="mt-6"
        />
      )}
    </div>
  );
};

export default SummaryForm;
